import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Activity, AlertTriangle, AlertCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useThresholds, useUpdateThresholds } from '../../api/thresholds';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import { FormPageSkeleton } from '@/components/ui/skeleton';
import ErrorState from '../../components/ui/ErrorState';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ThresholdsDto } from '../../types/api';

type FieldMeta = { key: keyof ThresholdsDto; label: string; description: string; step: string };

const OVERWORK_FIELDS: FieldMeta[] = [
  { key: 'loadVsBaselineRatio',   label: 'Load vs baseline ratio',   description: 'Active task points ÷ baseline points. Trips above this value. Default: 1.3.',                              step: '0.05' },
  { key: 'maxConcurrentTasks',    label: 'Max concurrent tasks',     description: 'Trips when the engineer has more active tasks than this. Default: 3.',                                       step: '1'    },
  { key: 'staleCycleMultiplier',  label: 'Stale cycle multiplier',   description: "Trips when an in-progress task exceeds this multiple of the engineer's baseline cycle days. Default: 1.5.", step: '0.1'  },
  { key: 'signalsRequiredToFlag', label: 'Signals required to flag', description: 'How many of the 3 signals must trip before flagging overwork. Default: 2.',                                 step: '1'    },
];

const T3_FIELDS: FieldMeta[] = [
  { key: 'escalationT3Days',       label: 'Days floor',          description: 'Absolute days-remaining floor for the T-3 warning. Default: 3.',                                                           step: '0.5'  },
  { key: 'escalationT3ElapsedPct', label: 'Elapsed % floor',     description: 'Proportional elapsed-time floor (0–1). T-3 fires at the later of this fraction elapsed or the days floor. Default: 0.60.', step: '0.05' },
  { key: 'escalationT3MinHours',   label: 'Min remaining hours', description: "Suppress the T-3 alert if fewer than this many hours remain. Default: 2.",                                                 step: '0.5'  },
];

const T1_FIELDS: FieldMeta[] = [
  { key: 'escalationT1Days',       label: 'Days floor',          description: 'Absolute days-remaining floor for the T-1 off-ramp prompt. Default: 1.',                                                    step: '0.5'  },
  { key: 'escalationT1ElapsedPct', label: 'Elapsed % floor',     description: 'Proportional elapsed-time floor (0–1). T-1 fires at the later of this fraction elapsed or the days floor. Default: 0.85.', step: '0.05' },
  { key: 'escalationT1MinHours',   label: 'Min remaining hours', description: "Suppress the T-1 alert if fewer than this many hours remain. Default: 1.",                                                  step: '0.5'  },
];

function ThresholdCard({
  icon: Icon, iconBg, iconCls, title, description, fields, register,
}: {
  icon: LucideIcon;
  iconBg: string;
  iconCls: string;
  title: string;
  description: string;
  fields: FieldMeta[];
  register: ReturnType<typeof useForm<ThresholdsDto>>['register'];
}) {
  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="flex items-start gap-2.5 border-b border-border px-5 py-4">
        <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${iconBg}`}>
          <Icon className={`h-4 w-4 ${iconCls}`} />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <CardContent className="space-y-5 p-5">
        {fields.map(({ key, label, description: desc, step }) => (
          <div key={key} className="space-y-1.5">
            <Label>{label}</Label>
            <Input type="number" step={step} min={0} {...register(key)} />
            <p className="text-xs text-muted-foreground">{desc}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function ThresholdsPage() {
  const { data: thresholds, isLoading, error, refetch } = useThresholds();
  const { mutate, isPending } = useUpdateThresholds();

  const { register, handleSubmit, reset, watch } = useForm<ThresholdsDto>();

  useEffect(() => { if (thresholds) reset(thresholds); }, [thresholds, reset]);

  const t3Days = Number(watch('escalationT3Days') ?? thresholds?.escalationT3Days ?? 3);
  const t3Pct  = Number(watch('escalationT3ElapsedPct') ?? thresholds?.escalationT3ElapsedPct ?? 0.6);
  const t1Days = Number(watch('escalationT1Days') ?? thresholds?.escalationT1Days ?? 1);
  const t1Pct  = Number(watch('escalationT1ElapsedPct') ?? thresholds?.escalationT1ElapsedPct ?? 0.85);

  const t3Crossover = t3Pct < 1 ? t3Days / (1 - t3Pct) : null;
  const t1Crossover = t1Pct < 1 ? t1Days / (1 - t1Pct) : null;

  const onSubmit = handleSubmit((values) => {
    mutate(
      {
        loadVsBaselineRatio:    Number(values.loadVsBaselineRatio),
        maxConcurrentTasks:     Number(values.maxConcurrentTasks),
        staleCycleMultiplier:   Number(values.staleCycleMultiplier),
        signalsRequiredToFlag:  Number(values.signalsRequiredToFlag),
        escalationT3Days:       Number(values.escalationT3Days),
        escalationT3ElapsedPct: Number(values.escalationT3ElapsedPct),
        escalationT1Days:       Number(values.escalationT1Days),
        escalationT1ElapsedPct: Number(values.escalationT1ElapsedPct),
        escalationT3MinHours:   Number(values.escalationT3MinHours),
        escalationT1MinHours:   Number(values.escalationT1MinHours),
      },
      { onSuccess: () => toast.success('Thresholds updated.') },
    );
  });

  if (isLoading) return <FormPageSkeleton fields={6} />;
  if (error)     return <ErrorState onRetry={refetch} />;

  return (
    <div className="max-w-5xl">
      <PageHeader title="Threshold configuration" description="Overwork detection parameters. Changes take effect immediately." />

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <ThresholdCard
            icon={Activity}
            iconBg="bg-primary/10"
            iconCls="text-primary"
            title="Overwork detection"
            description="Three independent signals. An engineer is flagged when the required number trip simultaneously."
            fields={OVERWORK_FIELDS}
            register={register}
          />
          <ThresholdCard
            icon={AlertTriangle}
            iconBg="bg-amber-50 dark:bg-amber-950/40"
            iconCls="text-amber-500"
            title="T-3 early warning"
            description="Fires at the later of the absolute days-remaining floor and the proportional elapsed-time floor."
            fields={T3_FIELDS}
            register={register}
          />
          <ThresholdCard
            icon={AlertCircle}
            iconBg="bg-red-50 dark:bg-red-950/40"
            iconCls="text-red-500"
            title="T-1 off-ramp"
            description="Fires at the later of the absolute days-remaining floor and the proportional elapsed-time floor."
            fields={T1_FIELDS}
            register={register}
          />
        </div>

        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300">
          <p className="mb-1 font-semibold">Live crossover</p>
          {t3Crossover !== null ? (
            <p>T-3: tasks shorter than <strong>{t3Crossover.toFixed(1)} days</strong> scale proportionally; longer tasks always get a fixed {t3Days}-day warning.</p>
          ) : (
            <p>T-3: elapsed % = 100% — threshold never reached proportionally.</p>
          )}
          {t1Crossover !== null ? (
            <p className="mt-1">T-1: tasks shorter than <strong>{t1Crossover.toFixed(1)} days</strong> scale proportionally; longer tasks always get a fixed {t1Days}-day off-ramp.</p>
          ) : (
            <p className="mt-1">T-1: elapsed % = 100% — threshold never reached proportionally.</p>
          )}
        </div>

        <Button type="submit" loading={isPending}>Save thresholds</Button>
      </form>
    </div>
  );
}
